import { useEffect, useRef, RefObject } from 'react';

/**
 * Calls `onClose` when a mousedown event fires outside the element referenced by `ref`.
 *
 * The listener is only attached while `enabled` is true. Defaults to `true`.
 *
 * @param ref      React ref pointing to the element that should be considered "inside".
 * @param onClose  Callback invoked on outside mousedown.
 * @param enabled  When false, the listener is detached. Defaults to true.
 */
export function useClickOutside<T extends HTMLElement>(
  ref: RefObject<T | null>,
  onClose: () => void,
  enabled: boolean = true
): void {
  // Keep latest onClose in a ref so the listener does not need to re-bind on every
  // render of the calling component (callers commonly pass inline arrows).
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!enabled) return;

    function handleMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onCloseRef.current();
      }
    }

    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [ref, enabled]);
}
